import { useEffect, useState } from 'react';

import { attributesApi } from '../api';

/** All attribute sets, for pickers. Cached at module level — these never change at runtime. */
let cachedList = null;

export function useAttributeSets() {
  const [sets, setSets] = useState(cachedList || []);
  const [loading, setLoading] = useState(!cachedList);

  useEffect(() => {
    if (cachedList) return;
    let cancelled = false;
    attributesApi
      .listSets()
      .then((list) => {
        cachedList = list;
        if (!cancelled) setSets(list);
      })
      .catch(() => setSets([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return { sets, loading };
}

const cachedSets = new Map();

/** One set with its full attribute definitions and grouping. */
export function useAttributeSet(setId) {
  const [set, setSet] = useState(setId ? cachedSets.get(setId) || null : null);
  const [loading, setLoading] = useState(Boolean(setId) && !cachedSets.has(setId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!setId) {
      setSet(null);
      setLoading(false);
      return undefined;
    }
    const cached = cachedSets.get(setId);
    if (cached) {
      setSet(cached);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    attributesApi
      .getSet(setId)
      .then((payload) => {
        cachedSets.set(setId, payload);
        if (!cancelled) setSet(payload);
      })
      .catch((cause) => !cancelled && setError(cause))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [setId]);

  return { attributeSet: set, loading, error };
}
